/**
 * AlertConfigDrawer - Right-side drawer for location alert rule management
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { X, Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import ConfirmDialog from '../shared/ConfirmDialog';
import { PermissionGate } from '@shared/auth';
import { StandardPermission } from '@shared/auth/permissions';
import {
  getAlertRules,
  createAlertRule,
  updateAlertRule,
  deleteAlertRule,
} from '../../services/locationService';

const METRIC_OPTIONS = [
  { value: 'complaint_count', label: 'Complaint Count' },
  { value: 'negative_rate', label: 'Negative Rate (0-1)' },
  { value: 'volume_spike', label: 'Volume Spike' },
];

const AREA_TYPE_OPTIONS = [
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'country', label: 'Country' },
];

const INITIAL_FORM = {
  rule_name: '',
  area_type: 'city',
  area_value: '',
  metric_type: 'complaint_count',
  threshold_value: '',
  time_window_hours: 24,
  is_active: true,
};

const AlertConfigDrawer = ({ isOpen, onClose, onRulesChanged }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const drawerRef = useRef(null);

  // Fetch rules
  const fetchRules = useCallback(async () => {
    setLoading(true);
    const res = await getAlertRules({ active_only: false });
    setLoading(false);
    if (res.success) setRules(res.data);
    else toast.error(res.error);
  }, []);

  useEffect(() => {
    if (isOpen) fetchRules();
  }, [isOpen, fetchRules]);

  // Slide-in animation
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  // Escape key
  const handleKeyDown = useCallback((e) => {
    if (deleteConfirm) return;
    if (e.key === 'Escape') onClose();

    if (e.key === 'Tab' && drawerRef.current) {
      const focusable = drawerRef.current.querySelectorAll(
        'button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, [onClose, deleteConfirm]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const resetForm = () => {
    setForm(INITIAL_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (rule) => {
    setForm({
      rule_name: rule.rule_name,
      area_type: rule.area_type,
      area_value: rule.area_value || '',
      metric_type: rule.metric_type,
      threshold_value: rule.threshold_value,
      time_window_hours: rule.time_window_hours,
      is_active: rule.is_active,
    });
    setEditingId(rule.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.rule_name.trim()) {
      toast.error('Rule name is required');
      return;
    }
    const threshold = parseFloat(form.threshold_value);
    if (isNaN(threshold) || threshold <= 0) {
      toast.error('Threshold must be a positive number');
      return;
    }

    setSaving(true);
    const payload = {
      ...form,
      threshold_value: threshold,
      area_value: form.area_value || null,
    };

    const res = editingId
      ? await updateAlertRule(editingId, payload)
      : await createAlertRule(payload);

    setSaving(false);
    if (res.success) {
      toast.success(editingId ? 'Rule updated' : 'Rule created');
      resetForm();
      fetchRules();
      onRulesChanged?.();
    } else {
      toast.error(res.error);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    const res = await deleteAlertRule(deleteConfirm.id);
    setDeleting(false);
    setDeleteConfirm(null);
    if (res.success) {
      toast.success('Rule deleted');
      fetchRules();
      onRulesChanged?.();
    } else {
      toast.error(res.error);
    }
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="alert-drawer-title">
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col transition-transform duration-300 ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 id="alert-drawer-title" className="text-base font-semibold text-gray-900">Alert Rules</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close alert drawer">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Add button — location_service alert-rule writes require
              analytics:reports (its WRITE_LOCATIONS alias). The form below only
              opens through this button or Edit, so gating both covers Save. */}
          {!showForm && (
            <PermissionGate permission={StandardPermission.Analytics.REPORTS}>
              <button
                onClick={() => { resetForm(); setShowForm(true); }}
                className="btn-primary text-sm w-full flex items-center justify-center space-x-1.5"
              >
                <Plus size={14} />
                <span>Add Alert Rule</span>
              </button>
            </PermissionGate>
          )}

          {/* Form */}
          {showForm && (
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <h3 className="text-xs font-semibold text-gray-500 uppercase">
                {editingId ? 'Edit Rule' : 'New Rule'}
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name</label>
                <input
                  type="text"
                  value={form.rule_name}
                  onChange={(e) => updateField('rule_name', e.target.value)}
                  className="input"
                  placeholder="e.g. High complaints in Mumbai"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Area Type</label>
                  <select
                    value={form.area_type}
                    onChange={(e) => updateField('area_type', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    {AREA_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Area (optional)</label>
                  <input
                    type="text"
                    value={form.area_value}
                    onChange={(e) => updateField('area_value', e.target.value)}
                    className="input"
                    placeholder="Leave blank for all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Metric</label>
                <select
                  value={form.metric_type}
                  onChange={(e) => updateField('metric_type', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  {METRIC_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Threshold</label>
                  <input
                    type="number"
                    step="any"
                    value={form.threshold_value}
                    onChange={(e) => updateField('threshold_value', e.target.value)}
                    className="input"
                    placeholder="e.g. 10"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Window (hours)</label>
                  <input
                    type="number"
                    min={1}
                    max={720}
                    value={form.time_window_hours}
                    onChange={(e) => updateField('time_window_hours', parseInt(e.target.value) || 24)}
                    className="input"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <button onClick={resetForm} className="text-sm text-gray-500 hover:text-gray-700">Cancel</button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn-primary text-sm disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          )}

          {/* Rules list */}
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-gray-400" size={20} />
            </div>
          ) : rules.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No alert rules configured</p>
          ) : (
            <div className="space-y-2">
              {rules.map((rule) => (
                <div key={rule.id} className="border border-gray-200 rounded-lg px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{rule.rule_name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {rule.metric_type.replace('_', ' ')} &ge; {rule.threshold_value} in {rule.time_window_hours}h
                        {rule.area_value ? ` (${rule.area_value})` : ' (all areas)'}
                      </p>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        rule.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {rule.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <PermissionGate permission={StandardPermission.Analytics.REPORTS}>
                        <button
                          onClick={() => handleEdit(rule)}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          aria-label={`Edit rule ${rule.rule_name}`}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(rule)}
                          className="p-1 text-gray-400 hover:text-red-500"
                          aria-label={`Delete rule ${rule.rule_name}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </PermissionGate>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        title="Delete Alert Rule"
        message={`Are you sure you want to delete "${deleteConfirm?.rule_name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
};

export default AlertConfigDrawer;
